import React, { useState, useEffect, useCallback } from 'react';
import { FileText, CheckCircle, XCircle, RotateCcw, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { templateRequestsAPI } from '../../services/api';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';

// Same status set/tones as RequestNewTemplatePage (the Manager-facing mirror
// of this queue) — kept identical so a request's pill looks the same on
// both sides of the approval flow.
const STATUS_CFG = {
  draft:                  { label: 'Draft',              tone: 'neutral' },
  pending_admin_approval: { label: 'Pending Approval',    tone: 'yellow' },
  approved:               { label: 'Approved',            tone: 'info' },
  rejected:               { label: 'Rejected',            tone: 'danger' },
  revision_requested:     { label: 'Revision Requested',  tone: 'orange' },
  generated:              { label: 'Generated',           tone: 'teal' },
  active:                 { label: 'Active',              tone: 'success' },
  archived:               { label: 'Archived',            tone: 'neutral' },
};

export default function AdminTemplateRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await templateRequestsAPI.getAll();
      setRequests(res.data.data || []);
    } catch { toast.error('Failed to load template requests'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleApprove(id) {
    setBusyId(id);
    try {
      await templateRequestsAPI.approve(id, {});
      toast.success('Approved — template added to the library');
      load();
    } catch (err) {
      if (!err._toasted) toast.error('Failed to approve');
    } finally { setBusyId(null); }
  }

  async function handleReject(id) {
    const admin_notes = window.prompt('Rejection reason (optional):') || '';
    setBusyId(id);
    try {
      await templateRequestsAPI.reject(id, { admin_notes });
      toast.success('Rejected');
      load();
    } catch (err) {
      if (!err._toasted) toast.error('Failed to reject');
    } finally { setBusyId(null); }
  }

  async function handleRevision(id) {
    const admin_notes = window.prompt('What needs to change?') || '';
    setBusyId(id);
    try {
      await templateRequestsAPI.requestRevision(id, { admin_notes });
      toast.success('Revision requested');
      load();
    } catch (err) {
      if (!err._toasted) toast.error('Failed to request revision');
    } finally { setBusyId(null); }
  }

  const pending = requests.filter(r => ['pending_admin_approval', 'revision_requested'].includes(r.status));
  const resolved = requests.filter(r => !['pending_admin_approval', 'revision_requested'].includes(r.status));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <PageHeader
        icon={FileText}
        title="Template Requests"
        subtitle="Manager requests for new announcement templates (CR Section 11)"
        actions={<Button variant="ghost" size="sm" icon={RefreshCw} onClick={load} />}
      />

      <h2 className="text-sm font-semibold text-gray-700 mb-2">Pending Review ({pending.length})</h2>
      {pending.length === 0 ? (
        <p className="text-sm text-gray-400 mb-6">Nothing to review</p>
      ) : (
        <div className="space-y-2 mb-6">
          {pending.map(r => {
            const st = STATUS_CFG[r.status] || STATUS_CFG.draft;
            const open = expanded === r.id;
            return (
              <div key={r.id} className="bg-white rounded-2xl border border-gray-200">
                <button
                  onClick={() => setExpanded(open ? null : r.id)}
                  className="w-full text-left px-4 py-3.5 flex items-center justify-between"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-gray-900">{r.template_name}</span>
                      <StatusBadge label={st.label} tone={st.tone} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">By {r.requester?.name || '—'} · {r.airline || r.zone_context || 'General'}</p>
                  </div>
                </button>
                {open && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Use Case</p>
                      <p className="text-sm text-gray-800 mb-2">{r.use_case}</p>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Wording</p>
                      <p className="text-sm text-gray-800">{r.wording}</p>
                    </div>
                    {r.variables?.length > 0 && (
                      <p className="text-xs text-gray-500">Variables: {r.variables.join(', ')}</p>
                    )}
                    {r.languages?.length > 0 && (
                      <p className="text-xs text-gray-500">Languages: {r.languages.join(', ')}</p>
                    )}
                    {r.example_announcement && (
                      <p className="text-xs text-gray-500 italic">Example: {r.example_announcement}</p>
                    )}
                    {r.notes && <p className="text-xs text-gray-500">Notes: {r.notes}</p>}
                    <div className="flex gap-2 pt-1">
                      <Button
                        variant="success"
                        icon={CheckCircle}
                        loading={busyId === r.id}
                        onClick={() => handleApprove(r.id)}
                        className="flex-1"
                      >
                        Approve
                      </Button>
                      {/* Light-orange "caution" treatment used only here — no
                          recurring need elsewhere for a Button variant. */}
                      <button
                        onClick={() => handleRevision(r.id)}
                        disabled={busyId === r.id}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-orange-100 hover:bg-orange-200 disabled:opacity-50 text-orange-700 text-sm font-semibold"
                      >
                        <RotateCcw size={15} /> Revise
                      </button>
                      <Button
                        variant="dangerLight"
                        icon={XCircle}
                        loading={busyId === r.id}
                        onClick={() => handleReject(r.id)}
                        className="flex-1"
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <h2 className="text-sm font-semibold text-gray-700 mb-2">Resolved</h2>
      {resolved.length === 0 ? (
        <p className="text-sm text-gray-400">No resolved requests yet</p>
      ) : (
        <div className="space-y-2">
          {resolved.map(r => {
            const st = STATUS_CFG[r.status] || STATUS_CFG.draft;
            return (
              <div key={r.id} className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-800">{r.template_name}</span>
                  <p className="text-xs text-gray-400">By {r.requester?.name || '—'}</p>
                </div>
                <StatusBadge label={st.label} tone={st.tone} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
